import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import useRouteRefresh from '../hooks/useRouteRefresh';
import { MAPBOX_TOKEN } from '../config/mapboxConfig';
import { getMarkerIcon } from '../utils/markerIcons';
import { mapboxMapHideAttributionClass } from '../utils/mapboxRtl';
import { acquireSharedMap, onSharedMapReady, releaseSharedMap } from '../utils/sharedMapInstance';
import { getCachedCircuitRoute, setCachedCircuitRoute, hashWaypoints } from '../utils/circuitRouteCache';
import logger from '@/utils/logger';

// Objet vide stable — évite de créer un nouveau {} à chaque rendu quand circuitColors n'est pas passé
const EMPTY_COLORS = {};

/** Tri des arrêts circuit pour tracé Mapbox : inbound_order (aller) ou outbound_order (retour). */
function sortKeyForCircuitStop(stop, direction) {
  const n = (v) => (v != null && v !== '' ? Number(v) : NaN);
  const o = n(direction === 'outbound' ? stop.outbound_order : stop.inbound_order);
  return Number.isNaN(o) ? 0 : o;
}

/**
 * Composant de carte interactive avec Mapbox GL JS
 * Remplace l'ancienne implémentation avec image statique
 * Avec mécanisme de rafraîchissement automatique des routes
 */
const InteractiveMap = ({
  routes = [],
  stops = [],
  vehicles = [],
  schools = [],
  circuits = [],
  circuitColors = EMPTY_COLORS,
  /** 'inbound' = ordre aller (matin), 'outbound' = ordre retour (soir) — colonnes route_stops */
  routeStopsDirection = 'inbound',
  /** Marqueurs d'arrêts (pastilles globales + pastilles numérotées sur les circuits) */
  showStopMarkers = true,
  onVehicleClick,
  style = {},
  height = '400px',
}) => {
  // Debug logging des props reçues
  // logger.debug('🗺️ InteractiveMap - Props reçues:', {
  //   circuitsLength: circuits?.length || 0,
  //   circuits: circuits,
  //   circuitColors: circuitColors,
  //   stopsLength: stops?.length || 0,
  //   vehiclesLength: vehicles?.length || 0
  // });

  const mapContainer = useRef(null);
  const map = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hasInitialCentered, setHasInitialCentered] = useState(false);
  const markersRef = useRef([]);
  const routeLayersRef = useRef(new Set()); // Track route layers for cleanup
  const routeSourcesRef = useRef(new Set()); // Track route sources for cleanup
  const circuitStopMarkersRef = useRef(new Map()); // circuit stop id -> Marker (ordre sur le tracé)
  const [lastCircuitsHash, setLastCircuitsHash] = useState(''); // Track circuits changes

  const stopIdsInCircuits = useMemo(() => {
    const set = new Set();
    (circuits || []).forEach((c) => {
      (c.stops || []).forEach((s) => {
        if (s?.id != null) set.add(String(s.id));
      });
    });
    return set;
  }, [circuits]);

  // Configuration Mapbox pour le Maroc
  const mapboxToken = MAPBOX_TOKEN;

  // Fonction pour nettoyer toutes les couches et sources de routes
  const cleanupRouteLayers = useCallback(() => {
    if (!map.current) return;

    // Layers d'abord (Mapbox interdit la suppression d'une source tant qu'une layer l'utilise)
    const layersToRemove = Array.from(routeLayersRef.current);
    layersToRemove.forEach((layerId) => {
      if (map.current?.getLayer?.(layerId)) {
        try {
          map.current.removeLayer(layerId);
        } catch {
          /* ignore */
        }
      }
    });
    routeLayersRef.current.clear();

    // Sources ensuite — synchrone, pas de setTimeout (le setTimeout créait une race condition)
    const sourcesToRemove = Array.from(routeSourcesRef.current);
    sourcesToRemove.forEach((sourceId) => {
      if (map.current?.getSource?.(sourceId)) {
        try {
          map.current.removeSource(sourceId);
        } catch {
          /* ignore */
        }
      }
    });
    routeSourcesRef.current.clear();
  }, []);

  // Fonction pour créer un hash des circuits pour détecter les changements
  const createCircuitsHash = useCallback((circuitsList, direction) => {
    if (!circuitsList || circuitsList.length === 0) return '';

    return circuitsList
      .map(
        (circuit) =>
          `${circuit.id}-${circuit.route_name}-${direction}-${circuit.stops?.length || 0}-${circuit.stops?.map((s) => `${s.id}-${s.inbound_order}-${s.outbound_order}`).join(',') || ''}`,
      )
      .join('|');
  }, []);

  const clearCircuitStopMarkers = () => {
    circuitStopMarkersRef.current.forEach((m) => {
      try {
        m.remove();
      } catch {
        /* ignore */
      }
    });
    circuitStopMarkersRef.current.clear();
  };

  const placeCircuitStopMarkers = useCallback(() => {
    if (!map.current || !circuits?.length) return;
    clearCircuitStopMarkers();
    const dirLabel = routeStopsDirection === 'outbound' ? 'retour (soir)' : 'aller (matin)';
    logger.info(
      `🗺️ [Direction] Placement marqueurs — sens: ${routeStopsDirection} (${dirLabel}), circuits: ${circuits.length}`,
    );
    circuits.forEach((circuit, circuitIndex) => {
      if (!circuit.stops?.length) return;
      const ordered = [...circuit.stops].sort(
        (a, b) => sortKeyForCircuitStop(a, routeStopsDirection) - sortKeyForCircuitStop(b, routeStopsDirection),
      );
      const circuitColor = circuitColors[circuit.id] || `hsl(${circuitIndex * 60}, 70%, 50%)`;

      ordered.forEach((stop, idx) => {
        const fullStop = stops.find((s) => String(s.id) === String(stop.id));
        if (fullStop?.status === 'inactive') return;
        const lat = parseFloat(stop.latitude);
        const lng = parseFloat(stop.longitude);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        const orderNum = idx + 1;
        const markerId = `cstop-${circuit.id}-${stop.id}`;

        const el = document.createElement('div');
        el.className = 'circuit-stop-marker';
        el.style.width = '21px';
        el.style.height = '21px';
        el.style.borderRadius = '50%';
        el.style.background = circuitColor;
        el.style.border = '2px solid #fff';
        el.style.boxShadow = '0 1px 5px rgba(0,0,0,0.35)';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.fontWeight = '800';
        el.style.fontSize = '10px';
        el.style.color = '#fff';
        el.style.cursor = 'pointer';
        el.textContent = String(orderNum);

        const name = stop.stop_name || stop.name || 'Arrêt';

        const students = fullStop?.students || [];
        const studentsHtml =
          students.length > 0
            ? `<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px;">
               <p style="margin:0 0 4px 0;font-size:12px;font-weight:600;color:#4B5563;">${students.length} élève(s) prévu(s)</p>
               <ul style="margin:0;padding:0;list-style:none;font-size:12px;color:#6B7280;max-height:100px;overflow-y:auto;">
                 ${students
                   .map((s) => {
                     const fullName = `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.name || 'Élève';
                     const inactiveBadge =
                       s.status === 'inactive'
                         ? `<span style="margin-left:4px;padding:0 4px;border-radius:3px;font-size:10px;background:#F3F4F6;color:#6B7280;">Inactif</span>`
                         : '';
                     return `<li style="margin:2px 0;padding-left:12px;position:relative;"><span style="position:absolute;left:0;top:5px;width:4px;height:4px;border-radius:50%;background-color:#3B82F6;"></span>${fullName}${inactiveBadge}</li>`;
                   })
                   .join('')}
               </ul>
             </div>`
            : `<div style="margin-top:8px;border-top:1px solid #eee;padding-top:8px;"><p style="margin:0;font-size:12px;color:#9CA3AF;font-style:italic;">Aucun élève assigné</p></div>`;

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .setPopup(
            new mapboxgl.Popup({ offset: 20 }).setHTML(
              `<div style="padding:10px;min-width:170px">
                    <div style="font-weight:600;font-size:14px;margin-bottom:4px">${name}</div>
                    <div style="font-size:12px;color:#64748b">Ordre <strong>${orderNum}</strong> — ${dirLabel}</div>
                    <div style="font-size:11px;color:#94a3b8;margin-top:4px">${circuit.route_name || ''}</div>
                    ${studentsHtml}
                  </div>`,
            ),
          )
          .addTo(map.current);

        circuitStopMarkersRef.current.set(markerId, marker);
      });
    });
  }, [circuits, routeStopsDirection, circuitColors, stops]);

  // Fonction pour créer un vrai itinéraire avec Mapbox Directions API
  const createRealRouteForCircuit = useCallback(
    async (circuit, orderedStops, schoolCoords, circuitColor, index) => {
      let waypoints = [];
      try {
        // logger.debug(`🗺️ Création itinéraire réel pour ${circuit.route_name}...`);

        // Créer la liste des waypoints : École → Arrêts → École
        waypoints = [
          schoolCoords, // Départ de l'école
          ...orderedStops.map((stop) => [stop.longitude, stop.latitude]),
          schoolCoords, // Retour à l'école
        ].filter((coord) => coord[0] && coord[1]);

        if (waypoints.length < 2) {
          logger.debug(`⚠️ Pas assez de waypoints pour ${circuit.route_name}`);
          return;
        }

        // Cache géométrie : le tracé ne dépend que des waypoints (école + arrêts
        // ordonnés). On évite de rappeler la Directions API tant que les arrêts /
        // l'ordre du circuit n'ont pas changé. Voir utils/circuitRouteCache.js.
        const routeHash = hashWaypoints(waypoints);
        let fullCoordinates = [];
        let totalDistance = 0;
        let totalDuration = 0;

        const cachedRoute = getCachedCircuitRoute(circuit.id, routeHash);
        if (cachedRoute) {
          logger.debug(`✅ [cache] Tracé circuit ${circuit.route_name} servi depuis le cache (0 appel Directions)`);
          fullCoordinates = cachedRoute.coordinates;
          totalDistance = cachedRoute.distance;
          totalDuration = cachedRoute.duration;
        } else {
          // L'API Mapbox Directions est limitée à 25 points par requête → chunks
          // avec chevauchement (le dernier point d'un chunk = premier du suivant).
          const MAX_WAYPOINTS_PER_REQUEST = 25;
          const chunks = [];
          for (let i = 0; i < waypoints.length; i += MAX_WAYPOINTS_PER_REQUEST - 1) {
            const chunk = waypoints.slice(i, i + MAX_WAYPOINTS_PER_REQUEST);
            if (chunk.length > 1) {
              chunks.push(chunk);
            }
          }

          for (let i = 0; i < chunks.length; i++) {
            const chunk = chunks[i];
            const coordinates = chunk.map((coord) => `${coord[0]},${coord[1]}`).join(';');
            // overview=full pour avoir le tracé exact des routes au lieu d'une ligne simplifiée
            const directionsUrl = `https://api.mapbox.com/directions/v5/mapbox/driving/${coordinates}?geometries=geojson&overview=full&access_token=${mapboxToken}`;

            logger.debug(
              `🔄 Appel API Directions pour ${circuit.route_name} (chunk ${i + 1}/${chunks.length}) avec ${chunk.length} points...`,
            );

            const response = await fetch(directionsUrl);
            const data = await response.json();

            if (!map.current) return;

            if (!response.ok || !data.routes || data.routes.length === 0) {
              logger.error(`❌ Erreur API Directions pour ${circuit.route_name} (chunk ${i + 1}/${chunks.length}):`, {
                status: response.status,
                statusText: response.statusText,
                data: data,
                url: directionsUrl.replace(mapboxToken, 'HIDDEN_TOKEN'),
              });
              // Si un chunk échoue, on fallback sur les lignes droites pour tout le circuit
              createStraightLineRoute(circuit, waypoints, circuitColor, index);
              return;
            }

            logger.debug(
              `✅ Succès API Directions pour ${circuit.route_name} (chunk ${i + 1}/${chunks.length}): ${data.routes[0].geometry.coordinates.length} points générés.`,
            );

            const route = data.routes[0];

            // Ajouter les coordonnées (en évitant de dupliquer le point de chevauchement)
            if (i === 0) {
              fullCoordinates = [...route.geometry.coordinates];
            } else {
              // Ignorer le premier point car il est déjà le dernier du chunk précédent
              fullCoordinates = [...fullCoordinates, ...route.geometry.coordinates.slice(1)];
            }

            totalDistance += route.distance;
            totalDuration += route.duration;
          }

          // Mémoriser le tracé calculé (mémoire + localStorage) pour les prochains
          // montages de carte et bascules aller/retour.
          setCachedCircuitRoute(circuit.id, routeHash, {
            coordinates: fullCoordinates,
            distance: totalDistance,
            duration: totalDuration,
          });
        }

        const routeGeometry = {
          type: 'LineString',
          coordinates: fullCoordinates,
        };

        // logger.debug(`✅ Itinéraire réel récupéré pour ${circuit.route_name}: ${route.distance}m, ${Math.round(route.duration/60)}min`);

        // Créer le GeoJSON avec la vraie géométrie
        const circuitGeoJSON = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: routeGeometry,
              properties: {
                circuitId: circuit.id,
                circuitName: circuit.route_name,
                color: circuitColor,
                distance: totalDistance,
                duration: totalDuration,
              },
            },
          ],
        };

        // Ajouter la source
        const sourceId = `circuit-${circuit.id}-source`;
        if (map.current.getSource(sourceId)) {
          // Si la source existe déjà, on la met à jour
          map.current.getSource(sourceId).setData(circuitGeoJSON);
        } else {
          map.current.addSource(sourceId, {
            type: 'geojson',
            data: circuitGeoJSON,
          });
        }

        // Ajouter la couche de bordure (plus large et plus sombre)
        const borderLayerId = `circuit-${circuit.id}-border-layer`;
        if (!map.current.getLayer(borderLayerId)) {
          map.current.addLayer({
            id: borderLayerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': '#1E293B', // Couleur sombre pour la bordure
              'line-width': 8, // Plus large que la ligne principale
              'line-opacity': 0.5, // Semi-transparent
            },
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
          });
        }

        // Ajouter la couche principale (couleur du circuit)
        const layerId = `circuit-${circuit.id}-layer`;
        if (!map.current.getLayer(layerId)) {
          map.current.addLayer({
            id: layerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': circuitColor,
              'line-width': 5, // Ligne continue épaisse
              'line-opacity': 0.85, // Légèrement transparent pour voir les rues
            },
            layout: {
              'line-join': 'round',
              'line-cap': 'round',
            },
          });
        }

        // Enregistrer les références pour le nettoyage
        routeLayersRef.current.add(borderLayerId);
        routeLayersRef.current.add(layerId);
        routeSourcesRef.current.add(sourceId);

        // logger.debug(`✅ Circuit réel ${circuit.route_name} tracé: ${Math.round(route.distance/1000)}km, ${Math.round(route.duration/60)}min`);
      } catch (error) {
        logger.error(`❌ Erreur création itinéraire pour ${circuit.route_name}:`, error);
        // Fallback vers les lignes droites
        createStraightLineRoute(circuit, waypoints, circuitColor, index);
      }
    },
    [mapboxToken],
  );

  // Fonction de fallback pour les lignes droites
  const createStraightLineRoute = useCallback((circuit, waypoints, circuitColor) => {
    // logger.debug(`🔄 Fallback lignes droites pour ${circuit.route_name}`);

    const circuitGeoJSON = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: waypoints,
          },
          properties: {
            circuitId: circuit.id,
            circuitName: circuit.route_name,
            color: circuitColor,
          },
        },
      ],
    };

    const sourceId = `circuit-${circuit.id}-source`;
    if (map.current.getSource(sourceId)) {
      map.current.getSource(sourceId).setData(circuitGeoJSON);
    } else {
      map.current.addSource(sourceId, {
        type: 'geojson',
        data: circuitGeoJSON,
      });
    }

    // Ajouter la couche de bordure pour le fallback aussi
    const borderLayerId = `circuit-${circuit.id}-border-layer`;
    if (!map.current.getLayer(borderLayerId)) {
      map.current.addLayer({
        id: borderLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#1E293B',
          'line-width': 6,
          'line-opacity': 0.4,
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      });
    }

    const layerId = `circuit-${circuit.id}-layer`;
    if (!map.current.getLayer(layerId)) {
      map.current.addLayer({
        id: layerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': circuitColor,
          'line-width': 4,
          'line-opacity': 0.7,
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      });
    }

    // Enregistrer les références pour le nettoyage
    routeLayersRef.current.add(borderLayerId);
    routeLayersRef.current.add(layerId);
    routeSourcesRef.current.add(sourceId);
  }, []);

  // Fonction pour recharger les circuits
  const reloadCircuits = useCallback(async () => {
    if (!mapLoaded || !map.current) {
      // logger.debug('🔄 Rechargement circuits ignoré: carte non chargée');
      return;
    }

    // Si pas de circuits, on nettoie tout et on s'arrête
    if (!circuits || circuits.length === 0) {
      // logger.debug('🧹 Nettoyage complet des circuits (liste vide)');
      clearCircuitStopMarkers();
      cleanupRouteLayers();
      return;
    }

    // logger.debug('🔄 Rechargement des circuits...');

    logger.info(`🔄 [Direction] reloadCircuits — sens: ${routeStopsDirection}, circuits: ${circuits.length}`);
    try {
      // Nettoyer les anciennes couches (synchrone) puis laisser Mapbox un tick pour finaliser
      cleanupRouteLayers();
      await new Promise((resolve) => setTimeout(resolve, 20));

      // Vérifier que la carte est toujours disponible après le délai
      if (!map.current) {
        logger.debug('⚠️ Carte non disponible après nettoyage');
        return;
      }

      // Traiter les circuits de manière asynchrone avec gestion d'erreurs
      const processCircuits = async () => {
        for (let index = 0; index < circuits.length; index++) {
          // Vérifier que la carte est toujours disponible à chaque itération
          if (!map.current) {
            logger.debug('⚠️ Carte supprimée pendant le traitement des circuits');
            break;
          }

          const circuit = circuits[index];
          // logger.debug(`🔄 Traitement circuit ${index + 1}:`, circuit);

          if (!circuit.stops || circuit.stops.length === 0) {
            logger.debug(`⚠️ Circuit ${circuit.id} ignoré: pas d'arrêts`);
            continue;
          }

          // Créer le tracé du circuit : École → Arrêts → École
          const schoolCoords = schools.length > 0 ? [schools[0].longitude, schools[0].latitude] : null;
          if (!schoolCoords) {
            logger.debug(`⚠️ Circuit ${circuit.id} ignoré: pas de coordonnées d'école`);
            continue;
          }

          const orderedStops = [...circuit.stops].sort(
            (a, b) => sortKeyForCircuitStop(a, routeStopsDirection) - sortKeyForCircuitStop(b, routeStopsDirection),
          );

          // Couleur du circuit
          const circuitColor = circuitColors[circuit.id] || `hsl(${index * 60}, 70%, 50%)`;

          try {
            // Créer le vrai itinéraire avec Mapbox Directions API
            await createRealRouteForCircuit(circuit, orderedStops, schoolCoords, circuitColor, index);
          } catch (error) {
            logger.error(`❌ Erreur traitement circuit ${circuit.route_name}:`, error);
            // Continuer avec le circuit suivant en cas d'erreur
          }

          // Petit délai entre les circuits pour éviter la surcharge
          await new Promise((resolve) => setTimeout(resolve, 10));
        }
      };

      // Lancer le traitement des circuits
      await processCircuits();

      logger.debug('✅ Rechargement des circuits terminé');
    } catch (error) {
      logger.error('❌ Erreur lors du rechargement des circuits:', error);
    }
  }, [mapLoaded, circuits, schools, circuitColors, cleanupRouteLayers, createRealRouteForCircuit, routeStopsDirection]);

  // Marqueurs d'arrêts circuit : mise à jour immédiate sur changement direction/données/visibilité
  useEffect(() => {
    if (!mapLoaded || !map.current) return;
    if (!showStopMarkers || !circuits?.length) {
      clearCircuitStopMarkers();
      return;
    }
    placeCircuitStopMarkers();
  }, [mapLoaded, showStopMarkers, circuits, placeCircuitStopMarkers]);

  // Utiliser le hook de rafraîchissement automatique des routes
  // routeStopsDirection et showStopMarkers sont gérés par leurs propres useEffect — pas besoin ici
  const { forceReload } = useRouteRefresh(reloadCircuits, [circuits, circuitColors, schools]);

  // Calculer le centre et les bounds
  const calculateBounds = () => {
    const allPoints = [
      ...schools.map((s) => [s.longitude, s.latitude]),
      ...stops.map((s) => [s.longitude, s.latitude]),
      ...vehicles.map((v) => [v.longitude, v.latitude]),
    ].filter((point) => point[0] && point[1]);

    if (allPoints.length === 0) {
      return {
        center: [-6.8416, 34.0209], // Rabat par défaut
        zoom: 12,
      };
    }

    if (allPoints.length === 1) {
      return {
        center: allPoints[0],
        zoom: 14,
      };
    }

    // Calculer les bounds
    const lngs = allPoints.map((p) => p[0]);
    const lats = allPoints.map((p) => p[1]);

    return {
      bounds: [
        [Math.min(...lngs), Math.min(...lats)], // Southwest
        [Math.max(...lngs), Math.max(...lats)], // Northeast
      ],
      fitBoundsOptions: { padding: 50 },
    };
  };

  // Initialiser la carte — instance Mapbox partagée (reuseMap), pas de map load
  // supplémentaire à chaque montage. Voir utils/sharedMapInstance.js.
  useEffect(() => {
    if (map.current) return; // Éviter la réinitialisation

    const mapConfig = calculateBounds();
    const view = mapConfig.bounds
      ? {
          bounds: mapConfig.bounds,
          fitBoundsOptions: mapConfig.fitBoundsOptions,
        }
      : { center: mapConfig.center, zoom: mapConfig.zoom };

    const owner = {};
    map.current = acquireSharedMap(mapContainer.current, view, owner);

    let cancelled = false;
    onSharedMapReady(() => {
      if (!cancelled) setMapLoaded(true);
    });

    return () => {
      cancelled = true;
      setMapLoaded(false); // Reset state on unmount

      // Nettoyer les couches/sources de circuits ajoutées par ce composant
      cleanupRouteLayers();

      // Nettoyer les marqueurs d'arrêts numérotés sur les tracés
      circuitStopMarkersRef.current.forEach((m) => {
        try {
          m.remove();
        } catch {
          /* ignore */
        }
      });
      circuitStopMarkersRef.current.clear();

      // Nettoyer les marqueurs écoles / véhicules / arrêts (suivis dans _markersById)
      if (map.current?._markersById) {
        Object.values(map.current._markersById).forEach((m) => {
          try {
            m.remove();
          } catch {
            /* ignore */
          }
        });
        map.current._markersById = {};
      }

      // Réinitialiser les références
      routeLayersRef.current.clear();
      routeSourcesRef.current.clear();
      markersRef.current = [];

      // Relâcher l'instance partagée (purge de sécurité + parking du conteneur,
      // la carte n'est pas détruite). No-op si une autre page a déjà acquis.
      releaseSharedMap(owner);
      map.current = null;
    };
  }, [mapboxToken]);

  // Ajouter les éléments à la carte avec des couches natives
  useEffect(() => {
    // Vérification robuste de l'état de la carte
    if (!map.current) {
      return;
    }

    // Si mapLoaded est faux mais que la carte est chargée (cas de race condition), on force le traitement
    if (!mapLoaded && !map.current.loaded()) {
      return;
    }

    // Si on arrive ici, c'est que la carte est prête (soit mapLoaded=true, soit map.loaded()=true)
    if (!mapLoaded && map.current.loaded()) {
      setMapLoaded(true);
    }

    // Au lieu de tout supprimer, on va mettre à jour les marqueurs existants
    // et créer seulement ceux qui n'existent pas encore.
    // On utilise un dictionnaire pour retrouver facilement les marqueurs par leur ID.
    if (!map.current._markersById) {
      map.current._markersById = {};
    }
    const markersById = map.current._markersById;

    // Garder une trace des IDs actuels pour supprimer ceux qui n'existent plus
    const currentSchoolIds = new Set();
    const currentVehicleIds = new Set();
    const currentStopIds = new Set();

    // 1. Gérer les écoles
    logger.debug(`Écoles à afficher sur la carte: ${schools.length}`);
    schools
      .filter((school) => school.latitude && school.longitude)
      .forEach((school) => {
        const id = `school-${school.id}`;
        currentSchoolIds.add(id);

        if (markersById[id]) {
          // Mettre à jour la position si elle a changé
          markersById[id].setLngLat([school.longitude, school.latitude]);
        } else {
          // Créer un nouveau marqueur
          const iconUrl = getMarkerIcon('school');
          logger.debug(`Création marqueur école ${school.name || school.id}, icône: ${iconUrl ? 'oui' : 'non'}`);

          const el = document.createElement('div');
          el.className = 'custom-school-marker';
          el.style.width = '48px';
          el.style.height = '48px';
          el.style.cursor = 'pointer';
          el.style.backgroundImage = `url('${iconUrl}')`;
          el.style.backgroundSize = 'contain';
          el.style.backgroundRepeat = 'no-repeat';
          el.style.backgroundPosition = 'center';

          const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
            .setLngLat([school.longitude, school.latitude])
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setHTML(
                `<div style="padding: 8px;">
                <h3 style="margin: 0 0 4px 0; font-weight: 600;">🏫 ${school.name}</h3>
                <p style="margin: 0; font-size: 12px; color: #666;">École</p>
              </div>`,
              ),
            )
            .addTo(map.current);

          markersById[id] = marker;
          markersRef.current.push(marker);
        }
      });

    // 2. Gérer les véhicules (bus)
    vehicles
      .filter((vehicle) => vehicle.latitude && vehicle.longitude)
      .forEach((vehicle) => {
        const id = `vehicle-${vehicle.id || vehicle.bus_number}`;
        currentVehicleIds.add(id);

        // Déterminer le statut du bus
        let busStatus = 'inactive';
        const rawStatus = vehicle.status ? vehicle.status.toLowerCase() : '';

        if (vehicle.ignition === false) {
          // Moteur coupé (Traccar) → garé indépendamment du statut de trajet
          busStatus = 'parked';
        } else if (rawStatus === 'active' || rawStatus === 'en_route' || rawStatus === 'moving') {
          busStatus = 'active';
        } else if (rawStatus === 'parked') {
          busStatus = 'parked';
        } else if (rawStatus === 'stopped') {
          busStatus = 'stopped';
        } else if (rawStatus === 'delayed') {
          busStatus = 'delayed';
        } else if (rawStatus === 'maintenance') {
          busStatus = 'maintenance';
        } else if (rawStatus === 'offline') {
          busStatus = 'offline';
        } else {
          busStatus = 'inactive';
        }

        const isBuffered = vehicle.buffered === true;
        const heading =
          vehicle.course !== undefined ? vehicle.course : vehicle.heading !== undefined ? vehicle.heading : null;
        const iconUrl = getMarkerIcon('bus', busStatus);

        const statusLabel =
          busStatus === 'active'
            ? 'En route'
            : busStatus === 'parked'
              ? 'Garé · Moteur coupé'
              : busStatus === 'stopped'
                ? 'Arrêté'
                : busStatus === 'offline'
                  ? 'Hors ligne'
                  : 'Inactif';
        const popupHTML = `<div style="padding: 8px;">
        <h3 style="margin: 0 0 4px 0; font-weight: 600;">🚌 ${vehicle.bus_number || 'Bus'}</h3>
        ${isBuffered ? `<div style="font-size:11px;color:#f59e0b;margin-bottom:4px">⚠️ Position en différé</div>` : ''}
        <p style="margin: 0; font-size: 12px; color: #666;">
          <strong>Chauffeur:</strong> ${vehicle.driver_name || 'Non assigné'}<br>
          <strong>Statut:</strong> ${statusLabel}<br>
          ${heading !== null ? `<strong>Cap:</strong> ${Math.round(heading)}°` : ''}
        </p>
      </div>`;

        if (markersById[id]) {
          // Mettre à jour le marqueur existant
          const marker = markersById[id];
          marker.setLngLat([vehicle.longitude, vehicle.latitude]);

          if (heading !== null) {
            marker.setRotation(heading);
          }

          const el = marker.getElement();
          el.style.backgroundImage = `url("${iconUrl}")`;
          el.style.outline = isBuffered ? '2px solid #f59e0b' : 'none';
          el.style.borderRadius = isBuffered ? '50%' : '';

          const popup = marker.getPopup();
          if (popup) {
            popup.setHTML(popupHTML);
          }
        } else {
          // Créer un nouveau marqueur
          const el = document.createElement('div');
          el.className = 'custom-bus-marker';
          el.style.width = '40px';
          el.style.height = '40px';
          el.style.cursor = 'pointer';
          el.style.backgroundImage = `url("${iconUrl}")`;
          el.style.backgroundSize = 'contain';
          el.style.backgroundRepeat = 'no-repeat';
          el.style.backgroundPosition = 'center';
          if (isBuffered) {
            el.style.outline = '2px solid #f59e0b';
            el.style.borderRadius = '50%';
          }

          el.addEventListener('click', () => {
            if (onVehicleClick) onVehicleClick(vehicle);
          });

          const markerOptions = { element: el, anchor: 'center' };
          if (heading !== null) {
            markerOptions.rotation = heading;
            markerOptions.rotationAlignment = 'map';
          }

          const marker = new mapboxgl.Marker(markerOptions)
            .setLngLat([vehicle.longitude, vehicle.latitude])
            .setPopup(new mapboxgl.Popup({ offset: 25 }).setHTML(popupHTML))
            .addTo(map.current);

          markersById[id] = marker;
          markersRef.current.push(marker);
        }
      });

    // 3. Les arrêts non assignés aux circuits ne sont pas affichés

    // 4. Nettoyer les marqueurs qui n'existent plus dans les données
    Object.keys(markersById).forEach((id) => {
      if (
        (id.startsWith('school-') && !currentSchoolIds.has(id)) ||
        (id.startsWith('vehicle-') && !currentVehicleIds.has(id)) ||
        (id.startsWith('stop-') && !currentStopIds.has(id))
      ) {
        const removed = markersById[id];
        removed.remove();
        delete markersById[id];
        markersRef.current = markersRef.current.filter((m) => m !== removed);
      }
    });
  }, [mapLoaded, schools, vehicles, stops, routes, stopIdsInCircuits]);

  // Gérer les changements de circuits avec rechargement automatique
  useEffect(() => {
    if (!mapLoaded || !map.current) return;

    const currentHash = createCircuitsHash(circuits, routeStopsDirection);
    logger.info(
      `🔄 [Direction] Hash check — sens: ${routeStopsDirection}, circuits: ${circuits?.length ?? 0}, changed: ${currentHash !== lastCircuitsHash}`,
    );

    if (currentHash !== lastCircuitsHash) {
      setLastCircuitsHash(currentHash);
      reloadCircuits();
    }
  }, [mapLoaded, circuits, circuitColors, routeStopsDirection, lastCircuitsHash, createCircuitsHash, reloadCircuits]);

  // Mettre à jour la vue (fitBounds) quand les données changent
  useEffect(() => {
    if (!mapLoaded || !map.current || hasInitialCentered) return;

    // Ne pas recentrer si on a déjà bougé manuellement (optionnel, ici on force le recentrage)
    // Pour l'instant on recentre uniquement si le nombre de points change significativement
    // ou au chargement initial des données

    const hasData = schools.length > 0 || stops.length > 0 || vehicles.length > 0;
    if (hasData) {
      const mapConfig = calculateBounds();
      if (mapConfig.bounds) {
        map.current.fitBounds(mapConfig.bounds, mapConfig.fitBoundsOptions);
        setHasInitialCentered(true);
      } else if (mapConfig.center) {
        map.current.flyTo({ center: mapConfig.center, zoom: mapConfig.zoom });
        setHasInitialCentered(true);
      }
    }
  }, [mapLoaded, schools.length, stops.length, vehicles.length, hasInitialCentered]);

  return (
    <div
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: '8px',
        height,
      }}
    >
      <div
        ref={mapContainer}
        className={mapboxMapHideAttributionClass}
        style={{
          width: '100%',
          height: '100%',
        }}
      />

      {/* Légende — masquée sur mobile */}
      <div className="hidden md:block">
        <div
          style={{
            position: 'absolute',
            bottom: 24,
            left: 12,
            background: 'rgba(255,255,255,0.95)',
            borderRadius: 8,
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            fontSize: 11,
            lineHeight: 1.8,
            zIndex: 10,
          }}
        >
          {[
            { color: '#1E40AF', label: 'École' },
            { color: '#22c55e', label: 'Bus actif' },
            { color: '#eab308', label: 'Bus arrêté' },
            { color: '#9ca3af', label: 'Garé · Moteur coupé' },
            { color: '#ef4444', label: 'Hors ligne' },
          ].map(({ color, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: color,
                  flexShrink: 0,
                }}
              />
              <span style={{ color: '#475569' }}>{label}</span>
            </div>
          ))}
          {/* Bouton rechargement circuits */}
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <span style={{ color: '#94a3b8', fontSize: 10 }}>Circuits</span>
            <button
              onClick={() => forceReload('manual')}
              style={{
                background: 'none',
                border: '1px solid #e2e8f0',
                borderRadius: 4,
                padding: '1px 6px',
                fontSize: 11,
                cursor: 'pointer',
                color: '#64748b',
              }}
              title="Recharger les tracés"
            >
              ↻
            </button>
          </div>
        </div>
      </div>

      {/* Indicateur de chargement */}
      {!mapLoaded && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 11,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="w-5 h-5 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
            <span>Chargement de la carte...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteractiveMap;
